import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { toast } from "sonner";
import { Smartphone, Gamepad2 } from "lucide-react";
import { startGoogleSignIn } from "../lib/googleAuth";
import { COUNTRIES } from "../lib/countries";
import { api } from "../lib/api";

const PLATFORMS = {
  mobile: { label: "Mobile", hint: "iOS / Android", Icon: Smartphone, accent: "#EA580C" },
  console: { label: "Console", hint: "PS5 / Steam", Icon: Gamepad2, accent: "#7C3AED" },
};

export default function Register() {
  const { register, user } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [search] = useSearchParams();
  const qp = (search.get("platform") || "mobile").toLowerCase();
  const [platform, setPlatform] = useState(PLATFORMS[qp] ? qp : "mobile");
  const [username, setUsername] = useState("");
  const [gameId, setGameId] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [password, setPassword] = useState("");
  const [clubName, setClubName] = useState("");
  const [nationality, setNationality] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user && user !== false) nav("/dashboard", { replace: true });
  }, [user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await register(username.trim(), gameId.trim(), whatsapp.trim(), password, platform);
      // Best-effort apply optional profile fields after register. We don't
      // hard-fail signup if these clash (e.g. duplicate club name) — user
      // can fix in /account.
      const cn = clubName.trim();
      const nat = nationality.trim();
      if (cn || nat) {
        try {
          await api.patch("/users/me/profile", { club_name: cn || null, nationality: nat || null });
        } catch (e2) {
          // Don't block — surface a soft hint
          if (cn && (e2?.response?.status === 409)) {
            toast.error(t("pc.clubTakenError"));
          }
        }
      }
      toast.success(t("reg.toast"));
      const next = search.get("next");
      nav(next && next.startsWith("/") ? next : "/dashboard", { replace: true });
    } catch (e) {
      setErr(e.message);
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative py-12" data-testid="register-page">
      <div
        className="absolute inset-0 -z-10 opacity-20 bg-cover bg-center"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1758410473735-c76baff30a79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxlc3BvcnRzJTIwYXJlbmElMjBkYXJrfGVufDB8fHx8MTc3NzM1NDUzNnww&ixlib=rb-4.1.0&q=85')" }}
      />
      <div className="absolute inset-0 -z-10 bg-[#F4F4F5]/80" />

      <div className="max-w-md mx-auto bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-t-2 border-t-[#7C3AED] p-8">
        <div className="flex flex-col items-center text-center mb-6">
          <img src="/logo-256.png" alt="ePlay" className="w-16 h-16 rounded-xl mb-3 shadow-[0_0_30px_rgba(0,229,255,0.2)]" data-testid="register-logo" />
          <div className="font-heading text-xl uppercase tracking-wider">ePlay</div>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-2">{t("reg.kicker")}</div>
        <h1 className="font-heading text-4xl uppercase mb-1">{t("reg.title")}</h1>
        <p className="text-sm text-[#71717A] mb-8">{t("reg.subtitle")}</p>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("reg.platform")}</label>
            <div className="grid grid-cols-2 gap-2" data-testid="register-platform-picker">
              {Object.entries(PLATFORMS).map(([key, P]) => {
                const selected = platform === key;
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`register-platform-${key}`}
                    onClick={() => setPlatform(key)}
                    className={`border px-3 py-3 flex items-center gap-3 text-left transition-colors ${
                      selected
                        ? "border-[#7C3AED] bg-[#7C3AED]/5"
                        : "border-[#E4E4E7] dark:border-[#2A2A30] hover:border-[#7C3AED]/50"
                    }`}
                    style={selected ? { borderColor: P.accent } : {}}
                  >
                    <P.Icon className="w-4 h-4" style={{ color: P.accent }} />
                    <div>
                      <div className="font-heading uppercase text-sm tracking-wider text-[#09090B] dark:text-[#F4F4F5]">{P.label}</div>
                      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#71717A]">{P.hint}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("reg.username")}</label>
            <input
              data-testid="register-username"
              className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-3 text-[#09090B] dark:text-[#F4F4F5] focus:outline-none focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED] font-body"
              value={username} onChange={(e) => setUsername(e.target.value)} required minLength={3}
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("reg.gameId")}</label>
            <input
              data-testid="register-gameid"
              className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-3 text-[#09090B] dark:text-[#F4F4F5] focus:outline-none focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED] font-body font-mono"
              value={gameId} onChange={(e) => setGameId(e.target.value)} required
              placeholder={t("reg.gameIdPlaceholder")}
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("reg.whatsapp")}</label>
            <input
              data-testid="register-whatsapp"
              type="tel"
              className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-3 text-[#09090B] dark:text-[#F4F4F5] focus:outline-none focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED] font-body font-mono"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              required
              minLength={6}
              maxLength={30}
              placeholder={t("reg.whatsappPlaceholder")}
              dir="ltr"
            />
            <div className="mt-2 text-xs text-[#71717A] leading-relaxed">
              {t("reg.whatsappHelp")}
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("reg.password")}</label>
            <input
              data-testid="register-password" type="password"
              className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-3 text-[#09090B] dark:text-[#F4F4F5] focus:outline-none focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED] font-body"
              value={password} onChange={(e) => setPassword(e.target.value)} required minLength={4}
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("reg.clubName")}</label>
            <input
              data-testid="register-club-name"
              className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-3 text-[#09090B] dark:text-[#F4F4F5] focus:outline-none focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED] font-body"
              value={clubName}
              onChange={(e) => setClubName(e.target.value)}
              maxLength={64}
              placeholder={t("reg.clubNamePh")}
            />
            <div className="mt-2 text-[11px] text-[#EA580C] leading-relaxed font-body">
              {t("pc.clubWarning")}
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("reg.nationality")}</label>
            <select
              data-testid="register-nationality"
              value={nationality}
              onChange={(e) => setNationality(e.target.value)}
              className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-3 text-[#09090B] dark:text-[#F4F4F5] focus:outline-none focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED] font-body"
            >
              <option value="">{t("reg.nationalityPh")}</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>
          {err && <div className="text-sm text-[#E11D48] font-mono" data-testid="register-error">// {err}</div>}
          <button type="submit"
            data-testid="register-submit"
            disabled={submitting}
            className="w-full bg-[#7C3AED] text-white font-heading uppercase tracking-widest px-8 py-3 hover:bg-[#6D28D9] transition-colors disabled:opacity-50"
          >
            {submitting ? t("reg.submitting") : t("reg.submit")}
          </button>
        </form>

        <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] font-mono text-[#71717A]">
          <span className="flex-1 h-px bg-[#E4E4E7] dark:bg-[#2A2A30]" />
          {t("google.continueWith")}
          <span className="flex-1 h-px bg-[#E4E4E7] dark:bg-[#2A2A30]" />
        </div>
        <button
          type="button"
          onClick={startGoogleSignIn}
          data-testid="register-google-btn"
          className="w-full inline-flex items-center justify-center gap-3 border border-[#E4E4E7] dark:border-[#2A2A30] bg-[#FFFFFF] dark:bg-[#1A1A1E] text-[#09090B] dark:text-[#F4F4F5] font-mono text-[11px] uppercase tracking-[0.25em] px-6 py-3 hover:border-[#7C3AED] hover:bg-[#FAFAFC] dark:hover:bg-[#0F0F14] transition-colors"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          {t("reg.signInWithGoogle")}
        </button>

        <div className="mt-8 pt-6 border-t border-[#E4E4E7] dark:border-[#2A2A30] text-sm text-[#71717A]">
          {t("reg.already")}{" "}
          <Link to="/login" className="text-[#7C3AED] hover:underline" data-testid="register-goto-login">{t("reg.loginLink")}</Link>.
        </div>
      </div>
    </div>
  );
}
